import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import {
  LayoutDashboard,
  FileText,
  Files,
  ScrollText,
  User,
  LogOut,
  ListChecks,
  Tv,
  ShieldCheck,
  Wallet,
  Ticket,
} from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";
import { useRegistration } from "@/context/RegistrationContext";

const candidateNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/dashboard/registration", label: "My Registration", icon: FileText },
  { to: "/dashboard/documents", label: "Documents", icon: Files },
  { to: "/dashboard/ack", label: "Registration Summary", icon: ScrollText },
  { to: "/dashboard/profile", label: "Profile", icon: User },
];

const adminNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/applications", label: "Applications", icon: ListChecks },
  { to: "/admin/registrations", label: "Registrations", icon: Files },
  { to: "/display", label: "Slot Display (TV)", icon: Tv },
];

// Finance only clears fees — no document/policy/application access.
const financeNav = [
  { to: "/admin/finance", label: "Fee Clearance", icon: Wallet },
];

// Check-in only assigns tokens — no document/policy/fee access.
const checkinNav = [
  { to: "/admin/checkin", label: "Check-in Desk", icon: Ticket },
];

// Admissions — Overview KPIs + report download, plus a read-only Applications roster.
const admissionsNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/applications", label: "Applications", icon: ListChecks },
];

export const DashboardSidebar = ({ variant = "candidate" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, logout } = useAdminAuth();
  const { resetAll } = useRegistration();
  const adminSession = variant === "admin" ? session : null;
  const nav =
    variant === "admin"
      ? adminSession?.role === "finance"
        ? financeNav
        : adminSession?.role === "checkin"
          ? checkinNav
          : adminSession?.role === "admissions"
            ? admissionsNav
            : adminNav
      : candidateNav;

  const doLogout = (e) => {
    e?.preventDefault?.();
    if (variant === "admin") {
      logout();
      navigate("/admin/login", { replace: true });
    } else {
      // Clear the in-memory registration state immediately — otherwise it
      // stays live in the shared context until a different application
      // number is actually submitted, leaving a window where this
      // application's data (name, documents, submitted status) is still
      // visible if the candidate logs back in with a different application.
      resetAll();
      navigate("/");
    }
  };

  return (
    <aside
      className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[#E5E7EB] bg-white h-screen sticky top-0"
      data-testid={`sidebar-${variant}`}
    >
      <div className="h-16 flex items-center px-6 border-b border-[#E5E7EB]">
        <Logo />
      </div>
      <div className="px-4 py-5">
        <div className="tiny-label px-2 mb-2">
          {variant === "admin" ? "Admin Console" : "Candidate Portal"}
        </div>
        <nav className="space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            const active =
              location.pathname === item.to ||
              (item.to !== "/admin" && item.to !== "/dashboard" && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? "bg-[#FBE7EA] text-[#B60F2A]"
                    : "text-[#6B7280] hover:bg-[#F7F7F7] hover:text-[#111]"
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? "text-[#B60F2A]" : ""}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
      {variant === "admin" && adminSession && (
        <div className="mx-4 mb-2 p-3 rounded-xl bg-[#F7F7F7] border border-[#E5E7EB]" data-testid="admin-session-card">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#FBE7EA] flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-[#B60F2A]" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#111] truncate">{adminSession.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">
                {adminSession.role === "saadmin"
                  ? "Student Affairs"
                  : adminSession.role === "superadmin"
                    ? "Super Admin"
                    : adminSession.role === "finance"
                      ? "Finance Office"
                      : adminSession.role === "checkin"
                        ? "Check-in Desk"
                        : adminSession.role === "admissions"
                          ? "Admissions"
                          : "Registrar Office"}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-auto p-4 border-t border-[#E5E7EB]">
        <button
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F7F7F7] hover:text-[#B60F2A]"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </aside>
  );
};
